/**
 * GraphQL auth.response values — keep in sync with glossapp `auth_responses.ts`.
 */
export const PLUGIN_AUTH_RESPONSE = {
  NEED_OTP: "need_otp",
  AUTHENTICATED: "authenticated",
  SUCCESS: "success",
  INCORRECT_PASSWORD: "Username or password incorrect",
  FAIL: "fail",
  OTP_ATTEMPTS_EXCEEDED: "otp_attempts_exceeded",
  REQUEST_OTP_FIRST: "request_otp first",
  OTP_SESSION_PENDING: "otp_session_pending",
  SERVICE_UNAVAILABLE: "service_unavailable",
  /** Successful OTP resend while session is open (glossapp `resendOtp`). */
  OTP_SENT: "sent",
} as const;

export type PluginAuthPhase = "credentials" | "authenticating" | "otp" | "syncing";

export type ParsedAuthResult =
  | { kind: "need_otp"; identifier: string }
  | { kind: "success"; identifier: string }
  | { kind: "credential_error"; message: string }
  | { kind: "otp_retry"; message: string; identifier: string }
  | { kind: "terminal"; message: string }
  | { kind: "pending"; identifier: string };

/** Auth completed — caller may run accounts/transactions sync. */
export function isPluginAuthSuccess(response: string | undefined | null): boolean {
  return (
    response === PLUGIN_AUTH_RESPONSE.SUCCESS || response === PLUGIN_AUTH_RESPONSE.AUTHENTICATED
  );
}

/** First wrong OTP — user can retry verify (matches server /more attempt/i). */
export function isRetryableOtpResponse(response: string | undefined | null): boolean {
  if (!response) {
    return false;
  }
  return /more attempt/i.test(response);
}

export function isTerminalOtpResponse(response: string | undefined | null): boolean {
  if (!response) {
    return false;
  }
  const terminal = new Set<string>([
    PLUGIN_AUTH_RESPONSE.FAIL,
    PLUGIN_AUTH_RESPONSE.OTP_ATTEMPTS_EXCEEDED,
    PLUGIN_AUTH_RESPONSE.REQUEST_OTP_FIRST,
    PLUGIN_AUTH_RESPONSE.SERVICE_UNAVAILABLE,
    "verify_timeout",
    "post_otp_ui_timeout",
    "scrape_timeout",
    "otp_idle_timeout",
  ]);
  if (terminal.has(response)) {
    return true;
  }
  return (
    response.includes("session has been closed") ||
    response.includes("took too long") ||
    response.includes("timed out")
  );
}

export function userMessageForAuthResponse(response: string): string {
  switch (response) {
    case "verify_timeout":
      return "Verification took too long. The session has been closed. Please connect again.";
    case "post_otp_ui_timeout":
      return "The portal took too long to respond after verification. The session has been closed. Please connect again.";
    case "scrape_timeout":
      return "Loading your account timed out. Please connect and try again.";
    case PLUGIN_AUTH_RESPONSE.OTP_ATTEMPTS_EXCEEDED:
      return "Too many incorrect codes. Please connect again.";
    case PLUGIN_AUTH_RESPONSE.SERVICE_UNAVAILABLE:
      return "Australian Super is temporarily unavailable. Please try again later.";
    default:
      return response;
  }
}

/**
 * Map raw GraphQL auth payload into UI actions.
 * @param sessionIdentifier — client-held session id (GraphQL often returns `identifier: null` on retryable wrong OTP).
 */
export function parsePluginAuthResponse(
  response: string | undefined | null,
  identifier: string | null | undefined,
  sessionIdentifier?: string | null,
): ParsedAuthResult {
  const code = (response ?? "").trim();
  const id = identifier?.trim() || sessionIdentifier?.trim() || "";

  if (code === PLUGIN_AUTH_RESPONSE.NEED_OTP && id) {
    return { kind: "need_otp", identifier: id };
  }

  if (isPluginAuthSuccess(code) && id) {
    return { kind: "success", identifier: id };
  }

  if (code === PLUGIN_AUTH_RESPONSE.INCORRECT_PASSWORD) {
    return { kind: "credential_error", message: code };
  }

  if (code === PLUGIN_AUTH_RESPONSE.OTP_SESSION_PENDING && id) {
    return { kind: "pending", identifier: id };
  }

  if (isRetryableOtpResponse(code)) {
    return { kind: "otp_retry", message: code, identifier: id };
  }

  if (isTerminalOtpResponse(code)) {
    return { kind: "terminal", message: userMessageForAuthResponse(code) };
  }

  if (code === PLUGIN_AUTH_RESPONSE.FAIL || !code) {
    return { kind: "terminal", message: "Authentication failed. Please try again." };
  }

  return { kind: "terminal", message: userMessageForAuthResponse(code) };
}

/** User-facing copy when resend OTP did not return `OTP_SENT` — session is treated as unusable. */
export function messageForResendOtpFailure(response: string | undefined | null): string {
  const code = (response ?? "").trim();
  if (code === PLUGIN_AUTH_RESPONSE.SERVICE_UNAVAILABLE) {
    return userMessageForAuthResponse(code);
  }
  if (
    code === PLUGIN_AUTH_RESPONSE.REQUEST_OTP_FIRST ||
    code === PLUGIN_AUTH_RESPONSE.OTP_ATTEMPTS_EXCEEDED ||
    code === "verify_timeout" ||
    code === "post_otp_ui_timeout" ||
    code === "otp_idle_timeout" ||
    code.includes("session has been closed") ||
    code.includes("took too long") ||
    code.includes("timed out")
  ) {
    return userMessageForAuthResponse(code);
  }
  return "We couldn't send a new verification code. Please connect again.";
}
